import React, { lazy, Suspense, useState, useEffect } from 'react'
import './App.css'
import { Grid, Container } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { useSelector, useDispatch } from 'react-redux'
import { IReduxState } from 'interfaces/IReduxState'
import { Switch, Route, Redirect, useHistory } from 'react-router-dom'
import API from '@aws-amplify/api'
import { getGroups, checkAccess, getCurrentUser } from 'shared/auth'
import ExportSalesOrder from 'components/salesOrder/exportSalesOrder'
import ExportOrder from 'components/order/exportOrder'
import ExportProduct from 'components/product/exportProduct'
import Home from 'components/home'
import SideDrawer from 'components/sideDrawer'
import Navbar from 'components/navbar'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import ManageIngredient from 'components/ingredient/manageIngredient'
import AddIngredient from 'components/ingredient/addIngredient'
import IngredientInventory from 'components/ingredient/ingredientInventory'
import PackagingInventory from 'components/packaging/packagingInventory'
import {
  VALUE_BARANG_JADI,
  VALUE_SETENGAH_JADI,
} from 'enums/stockCard'
import { Department } from 'enums/department'
import PremixProductionList from 'components/production/premixProductionList'
import HalfCompleteProductionList from 'components/production/halfCompleteProductionList'
import AddProductionPremix from 'components/production/addProductionPremix'
import AddProductionHalfComplete from 'components/production/addProductionHalfComplete'
import PremixInventory from 'components/premix/premixInventory'
import MixingInventory from 'components/production/mixingInventory'
import LabelInventory from 'components/production/labellingInventory'
import FillingInventory from 'components/production/fillingInventory'
import PurchaseOrderPage from 'components/purchaseOrder/purchaseOrderPage'
import PackagingRequest from 'components/packaging/packagingRequest'
import PackagingRequestList from 'components/packaging/packagingRequestList'
import IngredientRequestForm from './components/ingredient/ingredientRequest'
import IngredientRequestList from './components/ingredient/ingredientRequestList'
import ProductRequestForm from './components/product/productRequest'
import ProductRequestList from './components/product/productRequestList'
import BarangJadiRequestForm from './components/product/barangJadiRequest'
import BarangJadiRequestList from './components/product/barangJadiRequestList'
import Statistics from 'components/statistics/statistics'
import PremixRequestForm from 'components/premix/premixRequest'
import PremixRequestList from 'components/premix/premixRequestList'
import { SERVICES, ACTIONS, Service, Action } from 'enums/modules'

const Login = lazy(() => import('components/authentication/login'))
const Logout = lazy(() => import('components/authentication/logout'))
const AddPremix = lazy(() => import('components/premix/addPremix'))
const AddProduct = lazy(() => import('components/product/addProduct'))
const ManageProduct = lazy(() => import('components/product/manageProduct'))
const ProductInventory = lazy(
  () => import('components/product/productInventory')
)
const Discounts = lazy(() => import('components/product/manageDiscounts'))
const AddBrand = lazy(() => import('components/brand/addBrand'))
const ManageBrand = lazy(() => import('components/brand/manageBrand'))
const AddPackaging = lazy(() => import('components/packaging/addPackaging'))
const ManagePackaging = lazy(
  () => import('components/packaging/managePackaging')
)
const ManagePackagingParts = lazy(
  () => import('components/packaging/managePackagingParts')
)

const AddCustomer = lazy(() => import('components/customer/addCustomer'))
const ManageCustomer = lazy(() => import('components/customer/manageCustomer'))
const ExportCustomer = lazy(() => import('components/customer/exportCustomer'))
const AddEmployee = lazy(() => import('components/employee/addEmployee'))
const ManageEmployee = lazy(() => import('components/employee/manageEmployee'))
const ManageGroups = lazy(
  () => import('components/employee/manageEmployeeGroup')
)
const AddOrder = lazy(() => import('components/order/addOrder'))
const ManageOrder = lazy(() => import('components/order/manageOrder'))
const ManageInvoice = lazy(() => import('components/invoice/manageInvoice'))
const NotFound = lazy(() => import('components/notFound'))
const InvoiceQueue = lazy(() => import('components/invoice/invoiceQueue'))
const OrderQueue = lazy(() => import('components/order/orderQueue'))
const PacakgingOrderQueue = lazy(
  () => import('components/order/packagingOrderQueue')
)
const OrderList = lazy(() => import('components/order/orderList'))
const InvoiceList = lazy(() => import('components/invoice/invoiceList'))

const SalesOrderQueue = lazy(
  () => import('components/salesOrder/salesOrderQueue')
)
const ManageSalesOrder = lazy(
  () => import('components/salesOrder/manageSalesOrder')
)
const AddSalesOrder = lazy(() => import('components/salesOrder/addSalesOrder'))
const ManagePremix = lazy(() => import('components/premix/managePremix'))
const ExportInvoice = lazy(() => import('components/invoice/exportInvoice'))
const DeliveryOrderQueue = lazy(
  () => import('components/deliveryOrder/deliveryOrderQueue')
)
const ResetPassword = lazy(
  () => import('components/authentication/resetPassword')
)
const SalesReport = lazy(() => import('components/employee/salesReport'))
const CreateCheckinRecord = lazy(
  () => import('components/employee/createCheckinRecords')
)
const ManageCheckinRecord = lazy(
  () => import('components/employee/manageCheckinRecords')
)
const BatchInventory = lazy(() => import('components/premix/batchInventory'))
const CreateBatchPage = lazy(() => import('components/premix/createBatchPage'))
const ExportCSV = lazy(() => import('components/export/exportCSV'))
const ChangelogList = lazy(() => import('components/changelog/changelogList'))

const useStyles = makeStyles((theme) => ({
  image: {
    margin: 'auto',
    display: 'block',
  },
  container: {
    marginTop: '15px',
    height: '100vh',
  },
}))

const PrivateRoute = (props: {
  children?: React.ReactNode
  service: Service
  action: Action
  [key: string]: any
}) => {
  const { children, service, action, ...rest } = props
  const groupAccessMapping = useSelector(
    (state: IReduxState) => state.groupAccessMapping
  )
  const userGroups = useSelector((state: IReduxState) => state.userGroups || [])
  const hasAccess = checkAccess(userGroups, groupAccessMapping, service, action)

  return hasAccess ? (
    <Route {...rest}>{children}</Route>
  ) : (
    <Redirect to="/404" />
  )
}

function App() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  // const styles = useSpring({ y: isLoadingInitial ? null : 0 })

  const [isLoadingInitial, setIsLoadingInitial] = useState(true)

  useEffect(() => {
    if (process.env.REACT_APP_STAGE !== 'prod' && process.env.REACT_APP_STAGE !== 'staging') {
      document.title = '[DEV] Cawit Order Management'
    }
  }, [])

  React.useEffect(() => {
    onLoad()

    const handleWheel = () => {
      // @ts-ignore
      if (document.activeElement?.type === 'number') {
        // @ts-ignore
        document.activeElement?.blur()
      }
    }

    document.addEventListener('wheel', handleWheel)

    return () => {
      document.removeEventListener('wheel', handleWheel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onLoad() {
    try {
      await getCurrentUser(dispatch)
      const groups = await getGroups(dispatch)
      if (groups && Array.isArray(groups)) {
        await getGroupAccess(groups)
      }
      // Only mark as signed in when we actually retrieved group information
      if (groups && Array.isArray(groups)) {
        dispatch({ type: 'SIGN_IN_STATUS', payload: 'SIGNED_IN' })
      } else {
        // No groups -> not signed in, redirect to login if not already there
        dispatch({ type: 'SIGN_IN_STATUS', payload: 'SIGNED_OUT' })
        const path = window.location.pathname
        if (
          path !== '/login' &&
          path !== '/logout' &&
          path !== '/resetPassword'
        ) {
          history.push('/login')
        }
      }
    } catch (e) {
      console.log('Error during onLoad:', e)
      const path = window.location.pathname
      if (
        path !== '/login' &&
        path !== '/logout' &&
        path !== '/resetPassword'
      ) {
        history.push('/login')
      }
    } finally {
      setIsLoadingInitial(false)
    }
  }

  const getGroupAccess = async (groups: string[]) => {
    // TODO: implement caching
    const allPermissions: {
      [key: string]: { service: string; action: string }[]
    } = {}

    await Promise.all(
      groups.map(async (groupName) => {
        try {
          const response = await API.get(
            'products',
            `/employees/getGroupAccess?groupName=${groupName}`,
            {}
          )
          if (response && Array.isArray(response)) {
            response.forEach(
              (item: {
                groupName: string
                service: string
                action: string
              }) => {
                const lowerGroupName = item.groupName.toLowerCase()
                if (!(lowerGroupName in allPermissions)) {
                  allPermissions[lowerGroupName] = []
                }
                allPermissions[lowerGroupName].push({
                  service: item.service,
                  action: item.action,
                })
              }
            )
          }
        } catch (e) {
          console.error(`Failed to fetch access for group ${groupName}`, e)
        }
      })
    )


    if (Object.keys(allPermissions).length > 0) {
      dispatch({
        type: 'SET_GROUP_ACCESS_MAPPING',
        payload: allPermissions,
      })
    }
  }

  return (
    <React.Fragment>
      <Navbar />
      <SideDrawer />
      <Container className={classes.container} maxWidth="xl">
        {isLoadingInitial ? (
          <Route path="/">
            <Grid container justifyContent="center" alignItems="center">
              <SmoothLoadingIndicator size={120} />
            </Grid>
          </Route>
        ) : (
          <Suspense
            fallback={
              <Grid container justifyContent="center" alignItems="center">
                <SmoothLoadingIndicator size={120} />
              </Grid>
            }
          >
            <Switch>
              <Route path="/login" component={Login} />
              <Route path="/resetPassword">
                <ResetPassword />
              </Route>
              <Route path="/logout">
                <Logout />
              </Route>
              <PrivateRoute
                path="/product/manage"
                service={SERVICES.products}
                action={ACTIONS.update}
              >
                <ManageProduct />
              </PrivateRoute>
              <PrivateRoute
                path="/product/create"
                service={SERVICES.products}
                action={ACTIONS.create}
              >
                <AddProduct />
              </PrivateRoute>
              <PrivateRoute
                path="/product/discount"
                service={SERVICES.products}
                action={ACTIONS.discounts}
              >
                <Discounts />
              </PrivateRoute>
              <PrivateRoute
                path="/product/inventory/halfComplete/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventorySetengahJadi}
              >
                <ProductInventory
                  type={VALUE_SETENGAH_JADI}
                  department={Department.KOSMETIK}
                />
              </PrivateRoute>
              <PrivateRoute
                path="/product/inventory/halfComplete/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventorySetengahJadi}
              >
                <ProductInventory
                  type={VALUE_SETENGAH_JADI}
                  department={Department.OT}
                />
              </PrivateRoute>

              <PrivateRoute
                path="/product/inventory/complete/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryJadi}
              >
                <ProductInventory
                  type={VALUE_BARANG_JADI}
                  department={Department.KOSMETIK}
                />
              </PrivateRoute>
              <PrivateRoute
                path="/product/inventory/complete/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryJadi}
              >
                <ProductInventory
                  type={VALUE_BARANG_JADI}
                  department={Department.OT}
                />
              </PrivateRoute>

              <PrivateRoute
                path="/batch/inventory/kosmetik"
                service={SERVICES.batch}
                action={ACTIONS.inventoryKosmetik}
              >
                <BatchInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/batch/inventory/ot"
                service={SERVICES.batch}
                action={ACTIONS.inventoryOt}
              >
                <BatchInventory department={Department.OT} />
              </PrivateRoute>

              <Route exact path="/batch/inventory">
                <Redirect to="/batch/inventory/kosmetik" />
              </Route>


              <PrivateRoute
                path="/batch/create"
                service={SERVICES.batch}
                action={ACTIONS.create}
              >
                <CreateBatchPage />
              </PrivateRoute>

              <PrivateRoute
                path="/product/export"
                service={SERVICES.products}
                action={ACTIONS.export}
              >
                <ExportProduct />
              </PrivateRoute>
              <PrivateRoute
                path="/brand/manage"
                service={SERVICES.brands}
                action={ACTIONS.update}
              >
                <ManageBrand />
              </PrivateRoute>
              <PrivateRoute
                path="/brand/create"
                service={SERVICES.brands}
                action={ACTIONS.create}
              >
                <AddBrand />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/manage"
                service={SERVICES.packaging}
                action={ACTIONS.update}
              >
                <ManagePackaging />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/parts"
                service={SERVICES.packaging}
                action={ACTIONS.update}
              >
                <ManagePackagingParts />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/create"
                service={SERVICES.packaging}
                action={ACTIONS.create}
              >
                <AddPackaging />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/inventory/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryPackaging}
              >
                <PackagingInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/inventory/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryPackaging}
              >
                <PackagingInventory department={Department.OT} />
              </PrivateRoute>

              <PrivateRoute
                path="/packaging/request"
                service={SERVICES.packaging}
                action={ACTIONS.request}
              >
                <PackagingRequest />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/list"
                service={SERVICES.packaging}
                action={ACTIONS.requestList}
              >
                <PackagingRequestList />
              </PrivateRoute>
              <PrivateRoute
                path="/customer/manage"
                service={SERVICES.customers}
                action={ACTIONS.update}
              >
                <ManageCustomer />
              </PrivateRoute>
              <PrivateRoute
                path="/customer/create"
                service={SERVICES.customers}
                action={ACTIONS.create}
              >
                <AddCustomer />
              </PrivateRoute>
              <PrivateRoute
                path="/customer/export"
                service={SERVICES.customers}
                action={ACTIONS.export}
              >
                <ExportCustomer />
              </PrivateRoute>
              <PrivateRoute
                path="/employee/create"
                service={SERVICES.employees}
                action={ACTIONS.create}
              >
                <AddEmployee />
              </PrivateRoute>
              <PrivateRoute
                path="/employee/manage"
                service={SERVICES.employees}
                action={ACTIONS.update}
              >
                <ManageEmployee />
              </PrivateRoute>

              <PrivateRoute
                path="/employee/groups"
                service={SERVICES.employees}
                action={ACTIONS.createGroups}
              >
                <ManageGroups />
              </PrivateRoute>
              <PrivateRoute
                path="/employee/salesReport"
                service={SERVICES.employees}
                action={ACTIONS.salesReport}
              >
                <SalesReport />
              </PrivateRoute>
              <PrivateRoute
                path="/employee/createCheckin"
                service={SERVICES.employees}
                action={ACTIONS.createCheckin}
              >
                <CreateCheckinRecord />
              </PrivateRoute>
              <PrivateRoute
                path="/employee/manageCheckin"
                service={SERVICES.employees}
                action={ACTIONS.createCheckin}
              >
                <ManageCheckinRecord />
              </PrivateRoute>
              <PrivateRoute
                path="/order/create"
                service={SERVICES.orders}
                action={ACTIONS.create}
              >
                <AddOrder />
              </PrivateRoute>
              <PrivateRoute
                path="/order/manage"
                service={SERVICES.orders}
                action={ACTIONS.update}
              >
                <ManageOrder />
              </PrivateRoute>
              <PrivateRoute path="/order/queue" service={SERVICES.orders} action={ACTIONS.queue}>
                <OrderQueue />
              </PrivateRoute>
              <PrivateRoute
                path="/order/packaging/queue"
                service={SERVICES.orders}
                action={ACTIONS.packagingQueue}
              >
                <PacakgingOrderQueue />
              </PrivateRoute>
              <PrivateRoute path="/order/list" service={SERVICES.orders} action={ACTIONS.list}>
                <OrderList />
              </PrivateRoute>
              <PrivateRoute
                path="/order/export"
                service={SERVICES.orders}
                action={ACTIONS.export}
              >
                <ExportOrder />
              </PrivateRoute>
              <PrivateRoute
                path="/invoice/manage"
                service={SERVICES.invoices}
                action={ACTIONS.update}
              >
                <ManageInvoice />
              </PrivateRoute>
              <PrivateRoute
                path="/invoice/queue"
                service={SERVICES.invoices}
                action={ACTIONS.queue}
              >
                <InvoiceQueue />
              </PrivateRoute>
              <PrivateRoute
                path="/invoice/export"
                service={SERVICES.invoices}
                action={ACTIONS.export}
              >
                <ExportInvoice />
              </PrivateRoute>
              <PrivateRoute
                path="/invoice/list"
                service={SERVICES.invoices}
                action={ACTIONS.list}
              >
                <InvoiceList />
              </PrivateRoute>
              <PrivateRoute
                path="/salesOrder/create"
                service={SERVICES.salesOrders}
                action={ACTIONS.create}
              >
                <AddSalesOrder />
              </PrivateRoute>
              <PrivateRoute
                path="/salesOrder/manage/lws"
                service={SERVICES.salesOrders}
                action={ACTIONS.updateLws}
                render={() => <ManageSalesOrder isLws key="manage-lws" />}
              />
              <PrivateRoute
                path="/salesOrder/manage/ot"
                service={SERVICES.salesOrders}
                action={ACTIONS.updateOt}
                render={() => <ManageSalesOrder isOt key="manage-ot" />}
              />
              <PrivateRoute
                path="/salesOrder/factoryQueueLws"
                service={SERVICES.salesOrders}
                action={ACTIONS.factoryQueueLws}
                render={() => <SalesOrderQueue isLwsFactory key="factory-lws" />}
              />
              <PrivateRoute
                path="/salesOrder/factoryQueueOt"
                service={SERVICES.salesOrders}
                action={ACTIONS.factoryQueueOt}
                render={() => <SalesOrderQueue isOtFactory key="factory-ot" />}
              />
              <PrivateRoute
                path="/salesOrder/readyQueueLws"
                service={SERVICES.salesOrders}
                action={ACTIONS.readyQueueLws}
                render={() => <SalesOrderQueue isWarehouse isLwsFactory key="ready-lws" />}
              />
              <PrivateRoute
                path="/salesOrder/readyQueueOt"
                service={SERVICES.salesOrders}
                action={ACTIONS.readyQueueOt}
                render={() => <SalesOrderQueue isWarehouse isOtFactory key="ready-ot" />}
              />
              <PrivateRoute
                path="/salesOrder/export"
                service={SERVICES.salesOrders}
                action={ACTIONS.export}
              >
                <ExportSalesOrder />
              </PrivateRoute>
              <PrivateRoute
                path="/production/manageProductionFormPremix/kosmetik"
                service={SERVICES.production}
                action={ACTIONS.productionFormListPremixKosmetik}
              >
                <PremixProductionList department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/production/manageProductionFormPremix/ot"
                service={SERVICES.production}
                action={ACTIONS.productionFormListPremixOt}
              >
                <PremixProductionList department={Department.OT} />
              </PrivateRoute>
              <PrivateRoute
                path="/production/manageProductionFormHalfComplete"
                service={SERVICES.production}
                action={ACTIONS.manageProductionFormHalfComplete}
              >
                <HalfCompleteProductionList />
              </PrivateRoute>
              <PrivateRoute
                path="/production/createProductionFormPremix"
                service={SERVICES.production}
                action={ACTIONS.createProductionFormPremix}
              >
                <AddProductionPremix />
              </PrivateRoute>
              <PrivateRoute
                path="/production/createProductionHalfComplete"
                service={SERVICES.production}
                action={ACTIONS.createProductionHalfComplete}
              >
                <AddProductionHalfComplete />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/create"
                service={SERVICES.premix}
                action={ACTIONS.create}
              >
                <AddPremix />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/manage"
                service={SERVICES.premix}
                action={ACTIONS.update}
              >
                <ManagePremix />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/inventory/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryPremix}
              >
                <PremixInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/inventory/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryPremix}
              >
                <PremixInventory department={Department.OT} />
              </PrivateRoute>

              <PrivateRoute
                path="/ingredient/request"
                service={SERVICES.ingredient}
                action={ACTIONS.request}
              >
                <IngredientRequestForm />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/list"
                service={SERVICES.ingredient}
                action={ACTIONS.requestList}
              >
                <IngredientRequestList />
              </PrivateRoute>
              <PrivateRoute
                path="/product/request"
                service={SERVICES.products}
                action={ACTIONS.request}
              >
                <ProductRequestForm />
              </PrivateRoute>
              <PrivateRoute
                path="/product/list"
                service={SERVICES.products}
                action={ACTIONS.requestList}
              >
                <ProductRequestList />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/request"
                service={SERVICES.premix}
                action={ACTIONS.request}
              >
                <PremixRequestForm />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/list"
                service={SERVICES.premix}
                action={ACTIONS.requestList}
              >
                <PremixRequestList />
              </PrivateRoute>
              <PrivateRoute
                path="/barangJadi/request"
                service={SERVICES.barangJadi}
                action={ACTIONS.request}
              >
                <BarangJadiRequestForm />
              </PrivateRoute>
              <PrivateRoute
                path="/barangJadi/list"
                service={SERVICES.barangJadi}
                action={ACTIONS.requestList}
              >
                <BarangJadiRequestList />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/create"
                service={SERVICES.ingredient}
                action={ACTIONS.create}
              >
                <AddIngredient />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/manage"
                service={SERVICES.ingredient}
                action={ACTIONS.update}
              >
                <ManageIngredient />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/inventory"
                service={SERVICES.ingredient}
                action={ACTIONS.inventory}
              >
                <IngredientInventory />
              </PrivateRoute>
              <PrivateRoute
                path="/mixing/inventory/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryMixing}
              >
                <MixingInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/mixing/inventory/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryMixing}
              >
                <MixingInventory department={Department.OT} />
              </PrivateRoute>

              <PrivateRoute
                path="/filling/inventory/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryFilling}
              >
                <FillingInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/filling/inventory/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryFilling}
              >
                <FillingInventory department={Department.OT} />
              </PrivateRoute>

              <PrivateRoute
                path="/label/inventory/kosmetik"
                service={SERVICES.inventoryKosmetik}
                action={ACTIONS.inventoryLabel}
              >
                <LabelInventory department={Department.KOSMETIK} />
              </PrivateRoute>
              <PrivateRoute
                path="/label/inventory/ot"
                service={SERVICES.inventoryOt}
                action={ACTIONS.inventoryLabel}
              >
                <LabelInventory department={Department.OT} />
              </PrivateRoute>

              <PrivateRoute
                path="/deliveryOrder/queue"
                service={SERVICES.deliveryOrder}
                action={ACTIONS.queue}
              >
                <DeliveryOrderQueue />
              </PrivateRoute>
              <PrivateRoute
                path="/purchaseOrder/list"
                service={SERVICES.purchaseOrder}
                action={ACTIONS.list}
              >
                <PurchaseOrderPage />
              </PrivateRoute>
              <PrivateRoute path="/statistics" service={SERVICES.statistics} action={ACTIONS.view}>
                <Statistics />
              </PrivateRoute>
              <PrivateRoute path="/changelog" service={SERVICES.changelog} action={ACTIONS.view}>
                <ChangelogList />
              </PrivateRoute>
              <PrivateRoute path="/export/csv" service={SERVICES.batch} action={ACTIONS.export}>
                <ExportCSV />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/export"
                service={SERVICES.ingredient}
                action={ACTIONS.export}
              >
                <ExportCSV type="bahanBaku" />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/export"
                service={SERVICES.packaging}
                action={ACTIONS.export}
              >
                <ExportCSV type="kemasan" />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/export"
                service={SERVICES.premix}
                action={ACTIONS.export}
              >
                <ExportCSV type="premix" />
              </PrivateRoute>
              <PrivateRoute
                path="/batch/export"
                service={SERVICES.batch}
                action={ACTIONS.export}
              >
                <ExportCSV type="batch" />
              </PrivateRoute>
              <PrivateRoute
                path="/gudang/export"
                service={SERVICES.batch}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatGudang" />
              </PrivateRoute>
              <PrivateRoute
                path="/ingredient/export/history"
                service={SERVICES.ingredient}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatBahanBaku" />
              </PrivateRoute>
              <PrivateRoute
                path="/packaging/export/history"
                service={SERVICES.packaging}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatKemasan" />
              </PrivateRoute>
              <PrivateRoute
                path="/premix/export/history"
                service={SERVICES.premix}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatRuahan" />
              </PrivateRoute>
              <PrivateRoute
                path="/product/stj/export/history"
                service={SERVICES.products}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatSTJ" />
              </PrivateRoute>
              <PrivateRoute
                path="/product/jadi/export/history"
                service={SERVICES.products}
                action={ACTIONS.export}
              >
                <ExportCSV type="riwayatJadi" />
              </PrivateRoute>

              {/* Redirects for legacy/broken paths */}
              <Route path="/deliveryOrder/deliveryOrderQueue">
                <Redirect to="/deliveryOrder/queue" />
              </Route>
              <Route path="/ingredient/requestList">
                <Redirect to="/ingredient/list" />
              </Route>
              <Route path="/packaging/requestList">
                <Redirect to="/packaging/list" />
              </Route>
              <Route path="/premix/requestList">
                <Redirect to="/premix/list" />
              </Route>
              <Route path="/premix/request/list">
                <Redirect to="/premix/list" />
              </Route>
              <Route path="/product/requestList">
                <Redirect to="/product/list" />
              </Route>
              <Route path="/production/addProductionFormHalfComplete">
                <Redirect to="/production/createProductionHalfComplete" />
              </Route>

              <Route exact path="/">
                <Home />
              </Route>
              <Route>
                <NotFound />
              </Route>
            </Switch>
          </Suspense>
        )}
      </Container>
    </React.Fragment>
  );
}

export default App
